/**
 * Event Listeners Hook
 */

import { useEffect } from "react"
import type { OpenTab } from "../../features/document-tabs"
import type { usePerTabNavigationHistory } from "@/hooks/use-navigation-history"
import {
  resolveOpenDocumentTarget,
  type OpenDocumentEventDetail,
} from "@/lib/open-document-event"

export function useEventListeners(
  openTabs: OpenTab[],
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  activeTabId: string | null,
  setActiveTabId: (id: string | null) => void,
  setActiveDocumentPath: (path: string | null) => void,
  showSidebar: boolean,
  setShowSidebar: (show: boolean) => void,
  sidebarCollapsed: boolean,
  setSidebarCollapsed: (collapsed: boolean) => void,
  navigation: ReturnType<typeof usePerTabNavigationHistory>,
  handleDocumentClick: (
    documentId: string,
    event?: React.MouseEvent,
    options?: { searchQuery?: string },
  ) => Promise<void>
) {
  // Listen for window messages (multi-window support)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'TAB_MOVED') {
        const movedTabId = event.data.tabId;
        const tabExists = openTabs.some(t => t.id === movedTabId);

        if (tabExists) {
          const updatedTabs = openTabs.filter(t => t.id !== movedTabId);
          setOpenTabs(updatedTabs);

          if (activeTabId === movedTabId) {
            const nextTab = updatedTabs.find(t => t.id !== 'list') || updatedTabs[0];
            setActiveTabId(nextTab?.id || 'list');
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId]);

  // Listen for "show document in browse" events
  useEffect(() => {
    const handleShowInBrowse = (event: Event) => {
      // Opening a doc updates the tree in the background. Only an explicit
      // Reveal in sidebar (scrollToNode) unhides a collapsed sidebar.
      const scrollToNode = (event as CustomEvent<{ scrollToNode?: boolean }>).detail?.scrollToNode
      if (!scrollToNode) return
      if (!showSidebar) {
        setShowSidebar(true);
      }
      if (sidebarCollapsed) {
        setSidebarCollapsed(false);
      }
    };

    window.addEventListener('show-document-in-browse', handleShowInBrowse);

    // Listen for open-document events (used by integrations to open documents)
    const handleOpenDocument = async (
      event: CustomEvent<OpenDocumentEventDetail>,
    ) => {
      const { openInNewTab = false, clickEvent, searchQuery } = event.detail ?? {};
      const idOrPath = resolveOpenDocumentTarget(event.detail);

      const syntheticEvent = clickEvent
        ? ({
            metaKey: clickEvent.metaKey,
            ctrlKey: clickEvent.ctrlKey,
            preventDefault: () => {},
            stopPropagation: () => {},
          } as React.MouseEvent)
        : openInNewTab
          ? ({
              metaKey: true,
              ctrlKey: false,
              preventDefault: () => {},
              stopPropagation: () => {},
            } as React.MouseEvent)
          : undefined;

      await handleDocumentClick(
        idOrPath,
        syntheticEvent,
        searchQuery != null ? { searchQuery } : undefined,
      );
    };

    window.addEventListener('open-document', handleOpenDocument as unknown as EventListener);
    return () => {
      window.removeEventListener('show-document-in-browse', handleShowInBrowse);
      window.removeEventListener('open-document', handleOpenDocument as unknown as EventListener);
    };
  }, [showSidebar, sidebarCollapsed, setShowSidebar, setSidebarCollapsed, handleDocumentClick]);

  // NOTE: `document-path-updated` (dispatched after an AI/chat-driven move or rename) is handled
  // by useDocumentPathUpdatedEvent in resizable-layout.tab-sync.ts, not here. This hook used to
  // register a *second*, independent listener for the same event that unconditionally rewrote
  // `tab.id` to the new path. Since both listeners read the same `openTabs` snapshot and each
  // called `setOpenTabs` with a plain (non-functional) update, whichever ran second silently
  // clobbered the other's result - and the version that ran here also broke tab identity by
  // mutating a stable UUID `tab.id` into a path string, which forced spurious remounts and let a
  // duplicate tab get created for the same document (tab.id === documentId lookups elsewhere,
  // e.g. handleDocumentClick, stopped matching after the mutation). That duplicate-tab / dual
  // remount was the mechanism behind the overlapping/garbled editor render reported after
  // AI-driven renames. See docs/product/incidents/2026-07-08-document-rename-editor-desync.md
}
